// 3rd Party
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';

// Components

import {
  Div,
  FlexRow,
  H2,
  H3,
  MarketingButton,
  P,
  Span,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { selectDataRequestedAppointment } from 'connect/selectors';
import { Form } from 'opencare-crown';

import { useAnalyticsService } from 'services/AnalyticsService';

import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';

// Others

import CheckShieldSvg from '../../../../common/components/svgs/CheckShieldSvg';
import { StyledLock } from '../../../checkout-styled-components';
import { makeSelectRequestedAppointmentProvider } from '../../../connect/selectors';
import {
  checkCreditCard,
  checkCVV,
  checkExpiryDate,
  creditCards,
  getCreditCardType,
} from '../../../helpers/credit-card-helpers';

import { HoldCreditCard, NoShow } from './styled-components';

const analytics = new Analytics();

const CreditCardStep = ({ handleNextStep }) => {
  const [creditCardForm] = Form.useForm();

  const analyticsService = useAnalyticsService();

  const provider = useSelector(makeSelectRequestedAppointmentProvider);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);

  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    if (requestedAppointment.providerId) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.FORM,
        pageName: 'form-booking-uninsured',
        name: 'form-booking',
        section: 'uninsured',
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        ...analyticsService.analyticsData,
      });
    }
  }, [requestedAppointment]);

  const handleCardNumberChange = () => {
    setActiveCard(
      getCreditCardType(creditCardForm.getFieldValue('cardNumber')),
    );
  };

  const handleFormSubmit = (values) => {
    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: 'form-booking-uninsured',
      name: 'form-booking',
      section: 'uninsured',
      content: { 'credit-card': 'true' },
      practiceId: requestedAppointment.provider?.clinic,
      providerId: requestedAppointment.providerId,
    });

    handleNextStep(values);
  };

  return (
    <div>
      <H3 fontSize="20px" margin="-12px 0 0 auto">
        One last step...
        <StyledLock
          bottom="5px"
          margin="0 0 0 8px"
          size="16px"
          theme="filled"
          type="lock"
          role="presentation"
        />
      </H3>
      <P color={colors.slate} margin="0 0 20px 0">
        We need your payment information - here's why.
      </P>
      <NoShow margin="0 0 20px 0" padding="10px">
        <H2 color={colors.sageDark}>Our No Show Policy.</H2>
        <P>
          We get that life happens, and you can't always get to your
          appointment. All we ask is that you respect{' '}
          {provider ? provider.doctor.name : 'the dentist'} by cancelling or
          rescheduling at least{' '}
          <Span fontWeight="600">24 hours beforehand.</Span>
        </P>
        <P margin="10px 0 0 0">
          <Span fontWeight="600">Your card will not be charged</Span> the $50
          penalty if you cancel or reschedule in advance.
        </P>
      </NoShow>
      <Form
        className="sensitive-data"
        form={creditCardForm}
        onFinish={handleFormSubmit}
        name="creditcard"
      >
        <FloatingInput
          field="cardNumber"
          form={creditCardForm}
          label="Card Number"
          required
          validator={checkCreditCard}
          onChange={handleCardNumberChange}
        />
        <FlexRow margin="5px 0">
          {creditCards.map((card) => (
            <HoldCreditCard
              active={activeCard === card.type}
              key={card.type}
              margin="4px"
            >
              {card.component}
            </HoldCreditCard>
          ))}
        </FlexRow>
        <FlexRow>
          <Div width="70%">
            <FloatingInput
              field="expiryDate"
              form={creditCardForm}
              label="Expiry Date MM/YY"
              required
              validator={checkExpiryDate}
            />
          </Div>
          <Div width="29%" margin="0 0 0 10px">
            <FloatingInput
              field="cvv"
              form={creditCardForm}
              label="CVV"
              required
              validator={checkCVV}
            />
          </Div>
        </FlexRow>
        <FlexRow margin="8px 0">
          <CheckShieldSvg />
          <Div margin="0 0 0 10px">
            <P color={colors.slate}>
              100% Security Guarantee - 258-bit Encryption
            </P>
            <P color={colors.slate}>Your info will never be shared.</P>
          </Div>
        </FlexRow>
        <StyledFormItem marginbottom="0">
          <Div margin="10px 0 0 0">
            <MarketingButton
              type="primary"
              shape="round"
              size="large"
              block
              htmlType="submit"
            >
              Continue
            </MarketingButton>
          </Div>
        </StyledFormItem>
      </Form>
    </div>
  );
};

CreditCardStep.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
};

export default CreditCardStep;
